import { createUseStyles } from 'react-jss';
import color from 'color';

import { ThemeProps } from '@styles/theme';
import { flex, stickyFormBlock } from '@resources/mixins';

export const useStyles = createUseStyles((theme: ThemeProps) => ({
    stepNameActive: {},

    modal: {
        width: 'min(540px, calc(100vw - 32px))',
        maxHeight: 'calc(100dvh - 50px)',
        fallbacks: { maxHeight: 'calc(100vh - 50px)' },
        overflowY: 'auto',
    },

    modalHeading: {
        ...flex('row', 'space-between', 'center'),
        marginBottom: theme.spacing(7),
    },

    loader: {
        color: theme.colors.violet_primary,
        marginLeft: theme.spacing(2),
    },

    h2: {
        fontFamily: theme.font.secondary,
        fontSize: 24,
        lineHeight: 1.33,
        color: theme.colors.black,
        fontWeight: 'normal',
    },

    validationFailed: {
        ...flex('row', '', 'center'),
        marginBottom: theme.spacing(3),
        color: theme.colors.grey_text,
        fontSize: 16,
    },

    failIcon: {
        marginRight: theme.spacing(3),
        color: theme.colors.red,
    },

    tabsHeader: {
        ...flex('row', 'space-between', 'center'),
        marginBottom: theme.spacing(4),
        borderBottom: `1px solid ${theme.colors.grey_lighter}`,
    },

    deviceName: {
        fontSize: 16,
        color: theme.colors.black,
        marginBottom: theme.spacing(4),
        ...flex('row', 'flex-start', 'center'),
    },

    deviceImage: {
        marginRight: theme.spacing(3),
    },

    addDeviceButtonWrapper: {
        display: 'flex',
    },

    addDeviceButton: {
        color: theme.colors.violet_primary,
        cursor: 'pointer',

        '&:hover': {
            backgroundColor: theme.colors.icon_background,
        },
    },

    addDeviceButtonDisabled: {
        opacity: 0.5,
        cursor: 'default',
    },

    field: {
        marginBottom: theme.spacing(6),
    },

    fieldWrapper: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
    },

    dropdown: {
        margin: 0,
    },

    buttons: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: theme.spacing(8),
        flexWrap: 'wrap',
        gap: theme.spacing(3),
    },

    end: {
        justifyContent: 'flex-end',
    },

    button: {
        marginLeft: theme.spacing(5),
    },

    errors: {
        ...stickyFormBlock(theme.colors.white),
    },

    error: {
        backgroundColor: theme.colors.red,
        marginBottom: theme.spacing(2),
    },

    hidden: {
        display: 'none',
    },

    // ── Edit mode styles (UserModal-inspired) ─────────────────────────────────
    editModal: {
        height: 'calc(100dvh - 32px)',
        fallbacks: { height: 'calc(100vh - 32px)' },
        width: 'min(900px, calc(100vw - 32px))',
    },

    editModalContainer: {
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },

    editModalHeader: {
        flexShrink: 0,
        width: '100%',
        background: theme.isDark
            ? 'linear-gradient(135deg, #0d0a1e 0%, #1a1040 55%, #0a1a2e 100%)'
            : `linear-gradient(135deg, ${theme.colors.violet_darkest} 0%, ${theme.colors.violet_primary} 55%, ${theme.colors.deep_blue} 100%)`,

        padding: '22px 28px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: '-55px',
            right: '-35px',
            width: 210,
            height: 210,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color(theme.colors.blue_neon).alpha(0.22).string()} 0%, transparent 70%)`,
            filter: 'blur(22px)',
            pointerEvents: 'none',
        },
        '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-42px',
            left: '28%',
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${color(theme.colors.violet_primary).alpha(0.5).string()} 0%, transparent 70%)`,
            filter: 'blur(20px)',
            pointerEvents: 'none',
        },
    },

    editHeaderContent: {
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        zIndex: 1,
        position: 'relative',
        minWidth: 0,
    },

    editHeaderImage: {
        width: 52,
        height: 52,
        borderRadius: theme.radius,
        overflow: 'hidden',
        flexShrink: 0,
        cursor: 'pointer',
        position: 'relative',
        border: '2px solid rgba(255, 255, 255, 0.28)',
        boxShadow: '0 4px 14px rgba(0, 0, 0, 0.28)',

        '&:hover $editHeaderImageOverlay': {
            opacity: 1,
        },
    },

    editHeaderImageThumb: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        display: 'block',
    },

    editHeaderImagePlaceholder: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.10)',
    },

    editHeaderImageOverlay: {
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(60, 30, 100, 0.65)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.2s ease',
    },

    editHeaderTextBlock: {
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
    },

    editHeaderRight: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        zIndex: 1,
        position: 'relative',
        flexShrink: 0,
    },

    editCloseBtn: {
        zIndex: 1,
        position: 'relative',
        width: 36,
        height: 36,
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(8px)',
        border: '1.5px solid rgba(255, 255, 255, 0.18)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 0.2s ease, border-color 0.2s ease',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.22)',
            borderColor: 'rgba(255, 255, 255, 0.38)',
        },
        '& img': {
            width: 14,
            height: 14,
            filter: 'brightness(0) invert(1)',
        },
    },

    editModalBody: {
        flex: 1,
        minHeight: 0,
        width: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column' as const,
        padding: '22px 28px 0',
        background: theme.colors.grey_background,
    },

    editSection: {
        flexShrink: 0,
        background: theme.colors.white,
        borderRadius: 8,
        border: `1px solid ${theme.colors.grey_light}`,
        padding: '14px 20px 4px',
        marginBottom: 14,
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        overflow: 'visible',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.colors.violet_primary}, ${theme.colors.blue_primary})`,
            borderRadius: '8px 8px 0 0',
        },
    },

    editSectionTitle: {
        fontFamily: theme.font.secondary,
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase' as const,
        color: theme.colors.violet_primary,
        margin: '6px 0 14px',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        '&::after': {
            content: '""',
            flex: 1,
            height: 1,
            background: `linear-gradient(90deg, ${theme.colors.grey_light}, transparent)`,
        },
    },

    editField: {
        marginBottom: theme.spacing(6),
    },

    // Custom input styles matching FormikTextInput / TextInput appearance
    editInputLabel: {
        display: 'block',
        fontSize: 14,
        lineHeight: '20px',
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(2),
        fontFamily: theme.font.primary,
    },

    editInput: {
        borderRadius: theme.radius,
        width: '100%',
        fontSize: 16,
        lineHeight: '24px',
        minHeight: 44,
        border: `1.5px solid ${theme.colors.grey_light}`,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        color: theme.colors.black,
        fontFamily: theme.font.primary,
        background: theme.colors.white,
        boxSizing: 'border-box' as const,
        '&:focus': {
            boxShadow: 'none',
            borderColor: theme.colors.violet_primary,
            outline: 'none',
        },
        '&:hover': {
            borderColor: color(theme.colors.violet_primary).fade(0.62).string(),
        },
    },

    editTextarea: {
        resize: 'vertical' as const,
        minHeight: 90,
    },

    // Details section: image on left, fields on right
    editDetailsRow: {
        display: 'flex',
        flexDirection: 'row' as const,
        gap: theme.spacing(6),
        alignItems: 'flex-start',
        flexWrap: 'wrap' as const,
    },

    editProcedureImage: {
        position: 'relative',
        width: 220,
        height: 140,
        borderRadius: theme.radius,
        overflow: 'hidden',
        cursor: 'pointer',
        border: `2px dashed ${theme.colors.grey_light}`,
        flexShrink: 0,
        backgroundColor: theme.colors.grey_background,

        '&:hover $editProcedureImageOverlay': {
            opacity: 1,
        },
    },

    editProcedureImageThumb: {
        width: '100%',
        height: '100%',
        objectFit: 'contain' as const,
        display: 'block',
        background: theme.colors.grey_background,
    },

    // Used directly on <img> element as the placeholder when no image is set
    editProcedureImagePlaceholder: {
        width: '100%',
        height: '100%',
        objectFit: 'contain' as const,
        display: 'block',
        padding: 24,
        opacity: 0.35,
        boxSizing: 'border-box' as const,
    },

    editProcedureImageOverlay: {
        position: 'absolute',
        inset: 0,
        backgroundColor: 'rgba(70, 40, 120, 0.72)',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
        color: '#ffffff',
        opacity: 0,
        transition: 'opacity 0.2s ease',
        gap: theme.spacing(2),
        fontSize: 12,
        textAlign: 'center' as const,
        padding: `0 ${theme.spacing(3)}`,
    },

    editProcedureImageWrapper: {
        position: 'relative',
        flexShrink: 0,
        width: 220,
        height: 140,
    },

    editProcedureImageRemoveBtn: {
        position: 'absolute',
        top: 6,
        right: 6,
        zIndex: 2,
        width: 26,
        height: 26,
        borderRadius: '50%',
        background: color(theme.colors.red).alpha(0.88).string(),
        border: '1.5px solid rgba(255,255,255,0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: '#ffffff',
        boxShadow: '0 2px 6px rgba(0,0,0,0.28)',
        transition: 'background 0.18s ease, transform 0.15s ease',
        '&:hover': {
            background: color(theme.colors.red).darken(0.15).alpha(0.98).string(),
            transform: 'scale(1.1)',
        },
        '& svg': {
            fontSize: 14,
        },
    },

    editDetailsFields: {
        flex: 1,
        minWidth: 220,
    },

    editTypeChip: {
        display: 'inline-block',
        padding: `2px ${theme.spacing(3)}`,
        borderRadius: 4,
        border: `1px solid ${theme.colors.grey_light}`,
        fontSize: 12,
        fontFamily: theme.font.secondary,
        color: theme.colors.grey_text,
        marginBottom: theme.spacing(4),
        background: theme.colors.grey_background,
    },

    editHeaderTypeChip: {
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 3,
        background: 'rgba(255, 255, 255, 0.15)',
        border: '1px solid rgba(255, 255, 255, 0.32)',
        fontSize: 12,
        fontFamily: theme.font.secondary,
        letterSpacing: '0.04em',
        color: 'rgba(255, 255, 255, 0.92)',
        lineHeight: '20px',
        flexShrink: 0,
    },

    editTabsSection: {
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column' as const,
        background: theme.colors.white,
        borderRadius: 8,
        border: `1px solid ${theme.colors.grey_light}`,
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.colors.violet_primary}, ${theme.colors.blue_primary})`,
        },
    },

    editTabs: {
        flexShrink: 0,
        borderBottom: `1px solid ${theme.colors.grey_light}`,
        backgroundColor: 'rgba(120, 80, 200, 0.04)',
        paddingTop: 6,
        paddingLeft: theme.spacing(4),
    },

    editTabContent: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto' as const,
        '&::-webkit-scrollbar': {
            width: 4,
        },
        '&::-webkit-scrollbar-track': {
            width: 4,
            borderRadius: 2,
            backgroundColor: color(theme.colors.grey_darker).fade(0.85).string(),
        },
        '&::-webkit-scrollbar-thumb': {
            borderRadius: 2,
            backgroundColor: color(theme.colors.grey_darker).fade(0.4).string(),
        },
    },

    editBodyFooter: {
        flexShrink: 0,
        display: 'flex',
        justifyContent: 'space-between',
        padding: '12px 0 16px',
        flexWrap: 'wrap' as const,
        gap: theme.spacing(3),
        marginTop: 10,
        borderTop: `1px solid ${theme.colors.grey_light}`,
    },

    editFooterRight: {
        display: 'flex',
        gap: theme.spacing(3),
    },

    // Keep for legacy create-mode compat
    editDetailsCard: {},
    editImageZone: {},
    editImageThumb: {},
    editImagePlaceholder: {},
    editImageOverlay: {},
    editImageOverlayText: {},
    editDetailsActions: {},
    editTabsContainer: {},
    editModalFooter: {},

    processStepsWrapper: {
        marginBottom: theme.spacing(8),
        ...flex('row', 'flex-start', 'center'),
    },

    longDash: {
        display: 'inline-block',
        width: 20,
        height: 2,
        backgroundColor: theme.colors.grey_light,
        margin: `${0}px ${theme.spacing(4)}`,
    },

    stepName: {
        fontSize: 20,
        lineHeight: 1.4,
        color: theme.colors.grey_text,

        '&:hover': {
            cursor: 'pointer',
        },

        '&$stepNameActive': {
            color: theme.colors.black,
        },
    },

    tabsWrapper: {
        marginBottom: theme.spacing(4),
    },

    uploadPanel: {
        minHeight: 298,
        padding: `${theme.spacing(6)} ${0}`,
        ...flex('column', 'center', 'center'),
        border: `1px dashed ${color(theme.colors.violet_darker).fade(0.5)}`,
        borderRadius: theme.radius,
        outline: 'none',
    },

    uploadPanelInner: {
        width: 330,
        maxWidth: '100%',
    },

    uploadProcessButton: {
        fontSize: 20,
        lineHeight: 1.4,
        color: theme.colors.violet_primary,
        fontFamily: theme.font.secondary,
        marginRight: theme.spacing(6),
        cursor: 'pointer',
    },

    dragDropDescription: {
        fontSize: 16,
        color: theme.colors.black,
        ...flex('row', 'flex-start', 'center'),
    },

    dropProcedureText: {
        marginRight: theme.spacing(2),
    },

    uploadInProgress: {
        fontSize: 16,
        color: theme.colors.grey_text,
        fontFamily: theme.font.secondary,
        marginBottom: theme.spacing(8),
        ...flex('row', 'flex-start', 'center'),
    },

    uploadIcon: {
        marginRight: theme.spacing(3),
        color: theme.colors.grey_darker,
    },

    uploadIconProgress: {
        marginRight: theme.spacing(3),
        color: theme.colors.violet_primary,
    },

    progressBarWrapper: {
        marginBottom: theme.spacing(8),
    },

    confirmationPanel: {
        paddingTop: theme.spacing(4),
    },

    confirmationPanelText: {
        fontSize: 20,
        lineHeight: 1.4,
        color: theme.colors.black,
        marginBottom: theme.spacing(6),
    },

    confirmationPanelActions: {
        ...flex('row', 'flex-end', 'center'),

        '& button': {
            marginLeft: theme.spacing(5),
        },
    },
}));
